import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { RouterOutlet } from '@angular/router';
import { Apollo, gql } from 'apollo-angular';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  Observable,
  of,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
  Subject,
  takeUntil,
  tap,
  throwError
} from 'rxjs';

import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputSwitchModule } from 'primeng/inputswitch';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { PasswordModule } from 'primeng/password';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { Campaign } from '../../../models/campaign';
import { AgentState, STATE_COLOR } from '../../../models/chat';
import { STATUS_DISCONNECTED } from '../../../models/state';
import { Agent } from '../../../models/users';
import { AgentStateManagerService } from '../../../services/agent-state-manager.service';
import { AuthUserInput, AuthUserSyncService } from '../../../services/auth-user-sync.service';
import { CampaignService } from '../../../services/campaign.service';
import { ChangePasswordService } from '../../../services/change-password.service';
import { SharedDataService } from '../../../services/shared-data.service';
import { SupervisorCommunicationService } from '../../../services/supervisor-communication.service';
import { UserValidationService } from '../../../services/user-validation.service';
import { WorkspaceService } from '../../../services/workspace.service';
import { ConnectionState, ConnectionStatus, WebSocketManagerService } from '../../../connectivity';
import {
  AgentService,
  CreateAgentPayload,
  UpdateAgentPayload
} from '../../core/agent/services/agent.service';

const LS_ALL_STATES = 'all_status_agent';

const UPDATE_USER_MUTATION = gql`
  mutation updateUser($id: ID!, $password: String!) {
        updateUser(
          id: $id
          input: { password: $password }
        ) {
          id
        }
      }
    `;

type AgentDialogMode = 'create' | 'edit';

interface WorkspaceContext {
  id: string;
  name: string | null;
}

interface BackendErrorDetail {
  message?: string;
  code?: string;
  field?: string;
  value?: string;
}

@Component({
  selector: 'app-agents-supervisor',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    ButtonModule,
    ToastModule,
    RouterOutlet,
    PaginatorModule,
    InputTextModule,
    DropdownModule,
    TagModule,
    ToolbarModule,
    SkeletonModule,
    TooltipModule,
    DialogModule,
    PasswordModule,
    InputSwitchModule,
    TranslateModule,
  ],
  providers: [
    MessageService
  ],
  templateUrl: './agents-supervisor.component.html',
  styleUrl: './agents-supervisor.component.scss',
  standalone: true,
})
export class AgentsSupervisorComponent implements OnInit, OnDestroy {
  readonly createAgentEnabled = false;
  agents: Agent[] = [];
  filteredAgents: Agent[] = [];
  loading = false;

  first = 0;
  rows = 10;
  totalRecords = 0;
  rowsPerPageOptions: number[] = [5, 10, 20, 50];

  searchTerm = '';
  selectedStatus = '';

  statusOptions$: Observable<AgentState[]> | undefined;
  allStatusOptions: AgentState[] = [];

  showConfigDialog = false;
  selectedAgent: Agent | null = null;

  showPasswordDialog = false;
  passwordForm!: FormGroup;
  submittingPassword = false;

  showAgentDialog = false;
  agentForm!: FormGroup;
  submittingAgent = false;
  agentDialogMode: AgentDialogMode = 'create';
  agentDialogAgent: Agent | null = null;
  campaignOptions: Array<{ label: string; value: string }> = [];
  loadingCampaigns = false;
  currentWorkspaceId: string | null = null;
  currentWorkspaceName: string | null = null;

  supervisorId: string | null = null;

  private destroy$ = new Subject<void>();

  connectionStatus: ConnectionStatus = ConnectionStatus.DISCONNECTED;

  constructor(
    private agentService: AgentService,
    private messageService: MessageService,
    private workspaceService: WorkspaceService,
    private agentStateManagerService: AgentStateManagerService,
    private userValidationService: UserValidationService,
    private sharedDataService: SharedDataService,
    private changePasswordService: ChangePasswordService,
    private supervisorCommunicationService: SupervisorCommunicationService,
    private authUserSyncService: AuthUserSyncService,
    private campaignService: CampaignService,
    private fb: FormBuilder,
    private websocketManager: WebSocketManagerService,
    private apollo: Apollo,
    private translate: TranslateService,
  ) {
  }

  ngOnInit() {
    this.initializeStatusOptions();
    this.initPasswordForm();
    this.initAgentForm();
    this.registerForCommunicationMessages();

    this.agentStateManagerService.getAgentState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((states: string[]) => {
        this.updateStatusOptionsFromStates(states);
      });

    this.initializeWebSocketMonitoring();

    this.workspaceService.getWorkspace()
      .pipe(takeUntil(this.destroy$))
      .subscribe((workspace) => {
        const previousWorkspaceId = this.currentWorkspaceId;
        const workspaceLoaded = this.loadWorkspaceContext(workspace);

        if (!workspaceLoaded || !this.currentWorkspaceId) {
          return;
        }

        if (this.showAgentDialog) {
          this.agentForm.patchValue({
            workspace_id: this.currentWorkspaceId
          });
        }

        if (previousWorkspaceId !== this.currentWorkspaceId) {
          this.loadAgents();
        }
      });
  }

  private initializeStatusOptions() {
    const savedStates = JSON.parse(localStorage.getItem(LS_ALL_STATES) || '[]') as AgentState[];
    if (savedStates.length) {
      this.allStatusOptions = [this.createAllOption(), ...savedStates];
      this.statusOptions$ = of(this.allStatusOptions);
    } else {
      this.statusOptions$ = this.agentStateManagerService.getAgentState().pipe(
        map(labels => {
          const states = labels.map((label, i) => ({
            label,
            value: label,
            description: `Description for ${label}`,
            categoryId: `category-${i}`,
            color: STATE_COLOR[i % STATE_COLOR.length].color,
            backgroundColor: STATE_COLOR[i % STATE_COLOR.length].backgroundColor
          }));
          return [this.createAllOption(), ...states];
        }),
        tap(states => {
          this.allStatusOptions = states;
          localStorage.setItem(LS_ALL_STATES, JSON.stringify(states.slice(1)));
        }),
        shareReplay(1)
      );
    }
  }

  private initAgentForm(): void {
    this.agentForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      firstName: ['', [Validators.required, Validators.maxLength(80)]],
      lastName: ['', [Validators.required, Validators.maxLength(80)]],
      document: ['', [Validators.required, Validators.maxLength(30)]],
      version: ['1.0.0', [Validators.required, Validators.maxLength(30)]],
      ip: ['127.0.0.1', [Validators.required, Validators.maxLength(45)]],
      workspace_id: ['', Validators.required],
      password: ['', [
        Validators.minLength(8),
        this.uppercaseValidator,
        this.specialCharValidator
      ]],
      status: ['', Validators.required],
      enableOTP: [false]
    });

    this.agentForm.get('email')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.clearBackendFieldError('email'));
  }

  private registerForCommunicationMessages(): void {
    const supervisor = this.sharedDataService.getAgent();
    if (!supervisor) {
      console.warn('AgentsSupervisorComponent - No supervisor agent found in shared data');
      return;
    }

    this.supervisorId = supervisor.id;

    this.supervisorCommunicationService.agentRefresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshAgents();
      });

    this.supervisorCommunicationService.agentStatusUpdate$
      .pipe(takeUntil(this.destroy$))
      .subscribe((statusUpdate) => {
        this.updateAgentStatus(statusUpdate);
      });
  }

  private initializeWebSocketMonitoring(): void {
    console.log('Initializing WebSocket connection monitoring Supervisor Component');

    this.websocketManager.state$.pipe(
      takeUntil(this.destroy$),
      startWith(null),
      pairwise(),
      debounceTime(500)
    ).subscribe(([prev, curr]) => {
      if (!curr) {
        return;
      }

      this.connectionStatus = curr.status;
      this.handleConnectionStateChange(prev, curr);
    });

    this.websocketManager.state$.pipe(
      takeUntil(this.destroy$),
      filter(state => state.status === ConnectionStatus.CONNECTED),
      distinctUntilChanged((prev, curr) => prev.lastConnected === curr.lastConnected)
    ).subscribe();

    this.websocketManager.events$.pipe(
      takeUntil(this.destroy$),
      filter(event => {
        if (event.type === 'message' && event.data) {
          if (typeof event.data === 'object') {
            return event.data.type === 'circuit_breaker_closed' ||
              event.data.event === 'circuit_breaker_closed' ||
              (event.data.state === 'CLOSED' && event.data.state !== undefined);
          }
          if (typeof event.data === 'string') {
            return event.data === 'circuit_breaker_closed';
          }
        }
        return false;
      })
    ).subscribe(() => {
      this.loadAgents();
    });

    this.websocketManager.events$.pipe(
      takeUntil(this.destroy$),
      filter(event => event.type === 'status' || event.type === 'message'),
      filter(event => {
        if (event.data && typeof event.data === 'object') {
          return event.data.wasReconnection ||
            event.data.recoverySuccess ||
            (event.data.message && event.data.message.includes('PING: Response received')) ||
            (event.data.url && event.data.connectionTime !== undefined && event.data.attempt > 0);
        }
        return false;
      }),
      debounceTime(1000)
    ).subscribe(() => {
      this.loadAgents();
    });
  }

  private handleConnectionStateChange(previous: ConnectionState | null, current: ConnectionState) {
    if (!previous || !current) {
      return;
    }

    const previousConnected = previous.status === ConnectionStatus.CONNECTED;
    const currentConnected = current.status === ConnectionStatus.CONNECTED;

    if (!previousConnected && currentConnected && current.reconnectAttempts > 0) {
      this.loadAgents();
    }
  }

  private updateAgentStatus(payload: any): void {
    const agentId = payload.agent_id || payload.id || payload._id;
    const newStatus = payload.new_status || payload.status;
    const eventWorkspaceId = payload.workspace_id || payload.workspaceId || null;

    if (!agentId || !newStatus) {
      return;
    }

    if (this.currentWorkspaceId && eventWorkspaceId && String(eventWorkspaceId) !== String(this.currentWorkspaceId)) {
      return;
    }

    const agentIndex = this.agents.findIndex(agent => agent.id === agentId);
    if (agentIndex === -1) {
      return;
    }

    const oldStatus = this.agents[agentIndex].status;
    this.agents[agentIndex].status = newStatus;

    if (payload.last_update) {
      const lastUpdate = payload.last_update.replace(/Z$/, '');
      this.agents[agentIndex].last_update = new Date(lastUpdate);
    }
    if (payload.username) {
      this.agents[agentIndex].username = payload.username;
    }
    if (payload.email) {
      this.agents[agentIndex].email = payload.email;
    }

    this.applyFilters();

    if (oldStatus !== newStatus) {
      this.messageService.add({
        severity: 'info',
        summary: this.translate.instant('supervisor.agents.toast.statusUpdated.summary'),
        detail: `${this.agents[agentIndex].username}: ${newStatus}`,
        life: 2000
      });
    }
  }

  private updateStatusOptionsFromStates(states: string[]) {
    const mappedStates = states.map((label, i) => ({
      label,
      value: label,
      description: `Description for ${label}`,
      categoryId: `category-${i}`,
      color: STATE_COLOR[i % STATE_COLOR.length].color,
      backgroundColor: STATE_COLOR[i % STATE_COLOR.length].backgroundColor
    }));
    this.allStatusOptions = [this.createAllOption(), ...mappedStates];
    this.statusOptions$ = of(this.allStatusOptions);
    localStorage.setItem(LS_ALL_STATES, JSON.stringify(mappedStates));
  }

  private loadWorkspaceContext(source?: unknown): boolean {
    const workspace = this.resolveWorkspaceContext(source);
    if (!workspace) {
      return false;
    }

    this.currentWorkspaceId = workspace.id;
    this.currentWorkspaceName = workspace.name;
    return true;
  }

  private resolveWorkspaceContext(source?: unknown): WorkspaceContext | null {
    const candidates = [
      source,
      sessionStorage.getItem('workspace'),
      sessionStorage.getItem('workpsaceId'),
      sessionStorage.getItem('workspaceId'),
      localStorage.getItem('workspace'),
      localStorage.getItem('workpsaceId'),
      localStorage.getItem('workspaceId'),
    ];

    for (const candidate of candidates) {
      const parsed = this.parseWorkspaceCandidate(candidate);
      if (parsed) {
        return parsed;
      }
    }

    return null;
  }

  private parseWorkspaceCandidate(candidate: unknown): WorkspaceContext | null {
    if (!candidate) {
      return null;
    }

    let data: any = candidate;
    if (typeof candidate === 'string') {
      try {
        data = JSON.parse(candidate);
      } catch {
        return null;
      }
    }

    const id = data?.id || data?.workspaceId || data?.Id;
    if (!id) {
      return null;
    }

    return {
      id: String(id),
      name: data?.name || data?.workspaceName || null,
    };
  }

  private loadCampaignOptions(): void {
    if (!this.currentWorkspaceId) {
      this.campaignOptions = [];
      return;
    }

    this.loadingCampaigns = true;
    this.campaignService.getCampaignsByWorkspaceId(this.currentWorkspaceId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loadingCampaigns = false;
        })
      )
      .subscribe({
        next: (campaigns: Campaign[]) => {
          this.campaignOptions = (campaigns || [])
            .filter((campaign) => !!campaign.id)
            .map((campaign) => ({
              label: campaign.campaign_name || campaign.name || String(campaign.id),
              value: String(campaign.id)
            }));
        },
        error: (error: unknown) => {
          console.error('Error loading campaigns for agent form:', error);
          this.campaignOptions = [];
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('supervisor.agents.toast.campaignsUnavailable.summary'),
            detail: this.translate.instant('supervisor.agents.toast.campaignsUnavailable.detail'),
            life: 4000
          });
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAgents() {
    if (!this.currentWorkspaceId) {
      this.messageService.add({ severity: 'error', summary: this.translate.instant('supervisor.agents.toast.error'), detail: this.translate.instant('supervisor.agents.toast.workspaceUnavailable') });
      return;
    }

    this.loading = true;

    this.agentService.getAgentsByWorkspace(this.currentWorkspaceId, 'agent')
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: (agents: Agent[]) => {
          this.agents = agents;
          this.totalRecords = agents.length;
          this.applyFilters();
        },
        error: (error: any) => {
          console.error('Error loading agents:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('supervisor.agents.toast.error'),
            detail: this.translate.instant('supervisor.agents.toast.loadError')
          });
        }
      });
  }

  refreshAgents() {
    this.first = 0;
    this.loadAgents();
  }

  onPageChange(event: PaginatorState) {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 10;
  }

  onSearchChange(searchTerm: string) {
    this.searchTerm = searchTerm;
    this.first = 0;
    this.applyFilters();
  }

  onStatusChange(status: string) {
    this.selectedStatus = status;
    this.first = 0;
    this.applyFilters();
  }

  clearFilters() {
    this.searchTerm = '';
    this.selectedStatus = '';
    this.first = 0;
    this.applyFilters();
  }

  private applyFilters() {
    let filtered = [...this.agents];

    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      filtered = filtered.filter(agent =>
        agent.username.toLowerCase().includes(searchLower) ||
        agent.email.toLowerCase().includes(searchLower)
      );
    }

    if (this.selectedStatus) {
      filtered = filtered.filter(agent => agent.status === this.selectedStatus);
    }

    this.filteredAgents = filtered;
    this.totalRecords = filtered.length;
  }

  getStatusSeverity(status: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (status.toLowerCase()) {
      case 'disponible':
      case 'available':
      case 'ready':
        return 'success';
      case 'ocupado':
      case 'busy':
      case 'working':
        return 'warn';
      case 'no disponible':
      case 'desconectado':
      case 'disconnected':
      case 'offline':
        return 'danger';
      case 'pausa':
      case 'break':
      case 'pause':
        return 'info';
      default:
        return 'secondary';
    }
  }

  getStatusColor(status: string): string {
    const statusOption = this.allStatusOptions.find((option: AgentState) => option.value === status);
    return statusOption?.color || '#6c757d';
  }

  getStatusBackgroundColor(status: string): string {
    const statusOption = this.allStatusOptions.find((option: AgentState) => option.value === status);
    return statusOption?.backgroundColor || 'rgba(108, 117, 125, 0.1)';
  }

  getDisplayedAgents(): Agent[] {
    const startIndex = this.first;
    const endIndex = startIndex + this.rows;
    return this.filteredAgents.slice(startIndex, endIndex);
  }

  get availableStatusOptions(): AgentState[] {
    return this.allStatusOptions.filter(option => option.value !== '');
  }

  get agentDialogTitle(): string {
    return this.agentDialogMode === 'create'
      ? this.translate.instant('supervisor.agents.dialog.createTitle')
      : this.translate.instant('supervisor.agents.dialog.editTitle', { suffix: this.agentDialogAgent ? ' - ' + this.agentDialogAgent.username : '' });
  }

  get agentSubmitLabel(): string {
    return this.agentDialogMode === 'create'
      ? this.translate.instant('supervisor.agents.dialog.createSubmit')
      : this.translate.instant('supervisor.agents.dialog.editSubmit');
  }

  showCreateAgentDialog(): void {
    if (!this.createAgentEnabled) {
      this.messageService.add({
        severity: 'info',
        summary: this.translate.instant('supervisor.agents.toast.creationDisabled.summary'),
        detail: this.translate.instant('supervisor.agents.toast.creationDisabled.detail')
      });
      return;
    }

    if (!this.currentWorkspaceId) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('supervisor.agents.toast.workspaceRequired.summary'),
        detail: this.translate.instant('supervisor.agents.toast.workspaceRequired.detail')
      });
      return;
    }

    this.agentDialogMode = 'create';
    this.agentDialogAgent = null;
    this.showAgentDialog = true;
    this.resetAgentFormForCreate();
  }

  showEditAgentDialog(agent: Agent): void {
    this.agentDialogMode = 'edit';
    this.agentDialogAgent = agent;
    this.showAgentDialog = true;
    this.resetAgentFormForEdit(agent);
    this.loadAuthOtpSetting(agent.email, agent.workspace_id || this.currentWorkspaceId || '');
  }

  closeAgentDialog(): void {
    this.showAgentDialog = false;
    this.submittingAgent = false;
    this.agentDialogAgent = null;
    this.agentForm.reset();
    this.resetAgentPasswordValidators();
  }

  onSubmitAgent(): void {
    if (this.agentForm.invalid) {
      this.markFormGroupTouched(this.agentForm);
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('supervisor.agents.toast.incompleteForm.summary'),
        detail: this.translate.instant('supervisor.agents.toast.incompleteForm.detail')
      });
      return;
    }

    if (this.agentDialogMode === 'create') {
      if (!this.createAgentEnabled) {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('supervisor.agents.toast.creationDisabled.summary'),
          detail: this.translate.instant('supervisor.agents.toast.creationDisabled.detail')
        });
        return;
      }

      this.createAgent();
      return;
    }

    this.updateAgent();
  }

  private createAgent(): void {
    const payload = this.buildCreatePayload();
    if (!payload) {
      return;
    }

    this.submittingAgent = true;
    this.agentService.createAgent(payload)
      .pipe(
        switchMap(() => this.syncAuthUserAfterMsSave('create')),
        takeUntil(this.destroy$),
        finalize(() => {
          this.submittingAgent = false;
        })
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('supervisor.agents.toast.created.summary'),
            detail: this.translate.instant('supervisor.agents.toast.created.detail')
          });
          this.closeAgentDialog();
          this.refreshAgents();
        },
        error: (error: HttpErrorResponse | Error) => {
          if (error instanceof HttpErrorResponse) {
            this.handleAgentRequestError(error, 'crear');
            return;
          }

          this.handleAuthSyncFailure('crear', error);
        }
      });
  }

  private updateAgent(): void {
    if (!this.agentDialogAgent?.id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('supervisor.agents.toast.error'),
        detail: this.translate.instant('supervisor.agents.toast.agentNotFoundToEdit')
      });
      return;
    }

    const payload = this.buildUpdatePayload();
    this.submittingAgent = true;

    this.syncAuthUserAfterMsSave('edit')
      .pipe(
        switchMap(() => this.agentService.updateAgent(this.agentDialogAgent!.id, payload)),
        takeUntil(this.destroy$),
        finalize(() => {
          this.submittingAgent = false;
        })
      )
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('supervisor.agents.toast.updated.summary'),
            detail: this.translate.instant('supervisor.agents.toast.updated.detail')
          });
          this.closeAgentDialog();
          this.refreshAgents();
        },
        error: (error: HttpErrorResponse | Error) => {
          if (error instanceof HttpErrorResponse) {
            this.handleAgentRequestError(error, 'editar');
            return;
          }

          this.handleAuthSyncFailure('editar', error, true);
        }
      });
  }

  private buildCreatePayload(): CreateAgentPayload | null {
    const rawValue = this.agentForm.getRawValue();
    const password = String(rawValue.password || '').trim();

    if (!password) {
      this.passwordControl.setErrors({ required: true });
      this.passwordControl.markAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('supervisor.agents.toast.passwordRequired.summary'),
        detail: this.translate.instant('supervisor.agents.toast.passwordRequired.detail')
      });
      return null;
    }

    return {
      email: String(rawValue.email).trim(),
      username: this.buildUsernameForMsService(),
      // TODO: Replace hardcoded version when agent versioning is defined by product/backend.
      version: '1.0.0',
      ip: this.resolveClientIp(),
      workspace_id: this.resolveWorkspaceIdForPayload(),
      password,
      status: 'No Disponible',
      role: 'agent',
      campaigns: []
    };
  }

  private buildUpdatePayload(): UpdateAgentPayload {
    return {
      email: String(this.emailControl.value).trim(),
      username: this.buildUsernameForMsService(),
      // TODO: Replace hardcoded version when agent versioning is defined by product/backend.
      version: '1.0.0',
      ip: this.resolveClientIp(),
      workspace_id: this.resolveWorkspaceIdForPayload(),
      status: this.agentDialogAgent?.status || 'No Disponible',
      role: 'agent',
      campaigns: []
    };
  }

  private resetAgentFormForCreate(): void {
    this.agentForm.reset({
      email: '',
      firstName: '',
      lastName: '',
      document: '',
      version: '1.0.0',
      ip: this.resolveClientIp(),
      workspace_id: this.currentWorkspaceId || '',
      password: '',
      status: 'No Disponible',
      enableOTP: false
    });

    this.passwordControl.setValidators([
      Validators.required,
      Validators.minLength(8),
      this.uppercaseValidator,
      this.specialCharValidator
    ]);
    this.passwordControl.updateValueAndValidity();
  }

  private resetAgentFormForEdit(agent: Agent): void {
    const { firstName, lastName } = this.splitAgentName(agent.username || '');
    this.agentForm.reset({
      email: agent.email || '',
      firstName,
      lastName,
      document: '',
      version: '1.0.0',
      ip: this.resolveClientIp(),
      workspace_id: agent.workspace_id || this.currentWorkspaceId || '',
      password: '',
      status: agent.status || 'No Disponible',
      enableOTP: false
    });

    this.resetAgentPasswordValidators();
  }

  private loadAuthOtpSetting(email: string, workspaceId: string): void {
    const normalizedWorkspaceId = String(workspaceId || '').trim();
    if (!normalizedWorkspaceId) {
      console.warn('No fue posible consultar el usuario del workspace para edicion: workspace_id no disponible.');
      return;
    }

    this.authUserSyncService.findUserByEmailInWorkspace(email, normalizedWorkspaceId, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          if (user) {
            const patch: Record<string, unknown> = {};

            if (user.firstName) {
              patch['firstName'] = user.firstName;
            }

            if (user.lastName) {
              patch['lastName'] = user.lastName;
            }

            if (user.document) {
              patch['document'] = user.document;
            }

            if (typeof user.enableOTP === 'boolean') {
              patch['enableOTP'] = user.enableOTP;
            }

            if (Object.keys(patch).length) {
              this.agentForm.patchValue(patch);
            }
          }
        },
        error: (error) => {
          console.warn('No fue posible consultar los datos del usuario por workspace:', error);
        }
      });
  }

  private resolveWorkspaceIdForPayload(): string {
    return this.agentDialogAgent?.workspace_id || this.currentWorkspaceId || String(this.workspaceControl.value || '').trim();
  }

  private syncAuthUserAfterMsSave(mode: 'create' | 'edit') {
    const rawValue = this.agentForm.getRawValue();
    const authInput = this.buildAuthUserInput();
    const lookupEmail = mode === 'edit'
      ? (this.agentDialogAgent?.email || authInput.email)
      : authInput.email;
    const lookupWorkspaceId = this.resolveWorkspaceIdForPayload();

    if (!lookupWorkspaceId) {
      return throwError(() => new Error('Workspace no disponible para sincronizar auth/report.'));
    }

    return this.authUserSyncService.findUserByEmailInWorkspace(lookupEmail, lookupWorkspaceId, true).pipe(
      switchMap((existingUser) => {
        if (mode === 'edit') {
          if (!existingUser?.id) {
            return throwError(() => new Error('El usuario no existe en auth/report y no se intentará crear durante la edición.'));
          }

          return this.authUserSyncService.updateUser(existingUser.id, authInput);
        }

        if (existingUser?.id) {
          return this.authUserSyncService.updateUser(existingUser.id, authInput);
        }

        return this.authUserSyncService.createUser({
          ...authInput,
          password: mode === 'create'
            ? String(rawValue.password || '').trim()
            : this.buildFallbackPasswordForAuthUser()
        });
      }),
      catchError((error) => throwError(() => error instanceof Error ? error : new Error('No fue posible sincronizar auth/report')))
    );
  }

  private buildAuthUserInput(): AuthUserInput {
    const rawValue = this.agentForm.getRawValue();

    return {
      email: String(rawValue.email).trim(),
      firstName: String(rawValue.firstName || '').trim(),
      lastName: String(rawValue.lastName || '').trim(),
      document: String(rawValue.document || '').trim(),
      documentType: 'CC',
      phone: '3000000000',
      status: true,
      enableOTP: !!rawValue.enableOTP
    };
  }

  private buildUsernameForMsService(): string {
    const firstName = String(this.firstNameControl.value || '').trim();
    const lastName = String(this.lastNameControl.value || '').trim();
    return `${firstName} ${lastName}`.trim();
  }

  private splitAgentName(username: string): { firstName: string; lastName: string } {
    const normalized = username.trim().replace(/\s+/g, ' ');
    if (!normalized) {
      return {
        firstName: 'Agente',
        lastName: 'Xocial'
      };
    }

    const parts = normalized.split(' ');
    if (parts.length === 1) {
      return {
        firstName: parts[0],
        lastName: 'Agente'
      };
    }

    return {
      firstName: parts.slice(0, -1).join(' '),
      lastName: parts[parts.length - 1]
    };
  }

  private buildFallbackPasswordForAuthUser(): string {
    const emailPart = String(this.emailControl.value || 'Agent').split('@')[0].replace(/[^a-zA-Z0-9]/g, '');
    return `${emailPart || 'Agent'}#2026A`;
  }

  private resolveClientIp(): string {
    if (typeof window === 'undefined') {
      return '127.0.0.1';
    }

    const hostname = window.location.hostname?.trim();
    if (!hostname || hostname === 'localhost') {
      return '127.0.0.1';
    }

    return hostname;
  }

  private resetAgentPasswordValidators(): void {
    this.passwordControl.clearValidators();
    this.passwordControl.setValue('');
    this.passwordControl.updateValueAndValidity();
  }

  private handleAuthSyncFailure(action: 'crear' | 'editar', error: Error, blockMsUpdate = false): void {
    console.error(`Error al sincronizar auth/report al ${action} agente:`, error);
    const actionLabel = this.translate.instant(action === 'crear' ? 'supervisor.agents.action.create' : 'supervisor.agents.action.edit');
    if (blockMsUpdate) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('supervisor.agents.toast.updateFailed.summary'),
        detail: this.translate.instant('supervisor.agents.toast.updateFailed.detail'),
        life: 7000
      });
      return;
    }
    this.messageService.add({
      severity: blockMsUpdate ? 'error' : 'warn',
      summary: this.translate.instant('supervisor.agents.toast.partialSync.summary'),
      detail: this.translate.instant('supervisor.agents.toast.partialSync.detail', { action: actionLabel, message: error.message || '' }).trim(),
      life: 7000
    });
    this.closeAgentDialog();
    this.refreshAgents();
  }

  private handleAgentRequestError(error: HttpErrorResponse, action: 'crear' | 'editar'): void {
    const detail = this.extractBackendDetail(error);
    const friendlyMessage = detail.message || this.resolveFallbackErrorMessage(error.status, action);

    if (detail.field === 'email' || detail.field === 'email_workspace_id') {
      const currentErrors = this.emailControl.errors || {};
      this.emailControl.setErrors({
        ...currentErrors,
        backend: friendlyMessage
      });
      this.emailControl.markAsTouched();
    }

    this.messageService.add({
      severity: 'error',
      summary: this.resolveErrorSummary(error.status, action),
      detail: friendlyMessage,
      life: 5000
    });
  }

  private extractBackendDetail(error: HttpErrorResponse): BackendErrorDetail {
    const detail = error?.error?.detail;
    if (detail && typeof detail === 'object') {
      return detail as BackendErrorDetail;
    }

    return {};
  }

  private resolveErrorSummary(status: number, action: 'crear' | 'editar'): string {
    if (status === 409) {
      return this.translate.instant('supervisor.agents.requestError.duplicateEmail.summary');
    }

    if (status === 404) {
      return this.translate.instant('supervisor.agents.requestError.notFound.summary');
    }

    if (status >= 500) {
      return this.translate.instant('supervisor.agents.requestError.server.summary');
    }

    const actionLabel = this.translate.instant(action === 'crear' ? 'supervisor.agents.action.create' : 'supervisor.agents.action.edit');
    return this.translate.instant('supervisor.agents.requestError.generic.summary', { action: actionLabel });
  }

  private resolveFallbackErrorMessage(status: number, action: 'crear' | 'editar'): string {
    if (status === 409) {
      return this.translate.instant('supervisor.agents.requestError.duplicateEmail.detail');
    }

    if (status === 404) {
      return this.translate.instant('supervisor.agents.requestError.notFound.detail');
    }

    if (status >= 500) {
      return this.translate.instant('supervisor.agents.requestError.server.detail');
    }

    const actionLabel = this.translate.instant(action === 'crear' ? 'supervisor.agents.action.create' : 'supervisor.agents.action.edit');
    return this.translate.instant('supervisor.agents.requestError.generic.detail', { action: actionLabel });
  }

  private clearBackendFieldError(fieldName: string): void {
    const control = this.agentForm.get(fieldName);
    if (!control?.errors?.['backend']) {
      return;
    }

    const { backend, ...rest } = control.errors;
    control.setErrors(Object.keys(rest).length ? rest : null);
  }

  isLogoutDisabled(agent: Agent): boolean {
    return agent.status === STATUS_DISCONNECTED;
  }

  getAgentIconClass(agent: Agent): string {
    if (this.isLogoutDisabled(agent)) {
      return 'pi pi-user-minus';
    }

    switch (agent.status?.toLowerCase()) {
      case 'disponible':
      case 'available':
      case 'ready':
        return 'pi pi-check-circle';
      case 'ocupado':
      case 'busy':
      case 'working':
        return 'pi pi-clock';
      case 'pausa':
      case 'break':
      case 'pause':
        return 'pi pi-pause-circle';
      default:
        return 'pi pi-user';
    }
  }

  showConfigurationPanel(agent: Agent) {
    this.selectedAgent = agent;
    this.showConfigDialog = true;
  }

  closeConfigDialog() {
    this.showConfigDialog = false;
    this.selectedAgent = null;
  }

  confirmLogout() {
    if (this.selectedAgent) {
      this.logoutAgent(this.selectedAgent);
      this.closeConfigDialog();
    }
  }

  cancelLogout() {
    this.messageService.add({
      severity: 'info',
      summary: this.translate.instant('supervisor.agents.toast.cancelled.summary'),
      detail: this.translate.instant('supervisor.agents.toast.cancelled.detail')
    });
    this.closeConfigDialog();
  }

  acknowledgeDisconnected() {
    this.messageService.add({
      severity: 'info',
      summary: this.translate.instant('supervisor.agents.toast.info'),
      detail: this.translate.instant('supervisor.agents.toast.alreadyDisconnected')
    });
    this.closeConfigDialog();
  }

  private logoutAgent(agent: Agent) {
    this.messageService.add({
      severity: 'info',
      summary: this.translate.instant('supervisor.agents.toast.processing.summary'),
      detail: this.translate.instant('supervisor.agents.toast.processing.detail', { name: agent.username })
    });

    this.userValidationService.agent_logout_supervisor(agent.id.toString())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('supervisor.agents.toast.success'),
            detail: this.translate.instant('supervisor.agents.toast.logoutSuccess', { name: agent.username })
          });
          this.refreshAgents();
        },
        error: (error) => {
          console.error('Error logging out agent:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('supervisor.agents.toast.error'),
            detail: this.translate.instant('supervisor.agents.toast.logoutError', { name: agent.username, message: error.message || this.translate.instant('supervisor.agents.toast.unknownError') })
          });
        }
      });
  }

  private createAllOption(): AgentState {
    return {
      label: this.translate.instant('supervisor.agents.allStatuses'),
      value: '',
      description: this.translate.instant('supervisor.agents.allStatusesDescription'),
      categoryId: 'all',
      color: '#6c757d',
      backgroundColor: 'rgba(108, 117, 125, 0.1)'
    };
  }

  private initPasswordForm(): void {
    this.passwordForm = this.fb.group({
      newPassword: ['', [
        Validators.required,
        Validators.minLength(8),
        this.uppercaseValidator,
        this.specialCharValidator
      ]],
      confirmPassword: ['', Validators.required]
    }, { validators: this.passwordMatchValidator });
  }

  private uppercaseValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;
    return /[A-Z]/.test(value) ? null : { noUppercase: true };
  }

  private specialCharValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;
    const specialCharsRegex = /[!"$%&}'()*+,\-./:;<=>?@\[\\\]^_{|}~#]/;
    return specialCharsRegex.test(value) ? null : { noSpecialChar: true };
  }

  private passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordMismatch: true };
  }

  showChangePasswordDialog(agent: Agent): void {
    this.selectedAgent = agent;
    this.showPasswordDialog = true;
    this.initPasswordForm();
  }

  closePasswordDialog(): void {
    this.showPasswordDialog = false;
    this.selectedAgent = null;
    this.passwordForm.reset();
    this.submittingPassword = false;
  }

  onSubmitPassword(): void {
    if (this.passwordForm.invalid || !this.selectedAgent) {
      this.markFormGroupTouched(this.passwordForm);
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('supervisor.agents.toast.validationError.summary'),
        detail: this.translate.instant('supervisor.agents.toast.validationError.detail')
      });
      return;
    }

    if (!this.selectedAgent.id || !this.selectedAgent.email || !this.selectedAgent.username) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('supervisor.agents.toast.error'),
        detail: this.translate.instant('supervisor.agents.toast.incompleteAgentData')
      });
      return;
    }

    this.submittingPassword = true;
    const { newPassword } = this.passwordForm.value;

    const agentId = this.selectedAgent.id;
    const agentEmail = this.selectedAgent.email;
    const agentUsername = this.selectedAgent.username;
    const agentWorkspaceId = String(this.selectedAgent.workspace_id || this.currentWorkspaceId || '').trim();

    if (!agentWorkspaceId) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('supervisor.agents.toast.workspaceNotAvailable.summary'),
        detail: this.translate.instant('supervisor.agents.toast.workspaceNotAvailable.detail')
      });
      this.submittingPassword = false;
      return;
    }

    this.changePasswordService.changePasswordByAdmin(agentId, newPassword)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('supervisor.agents.toast.success'),
            detail: this.translate.instant('supervisor.agents.toast.passwordUpdated', { name: agentUsername })
          });
          this.closePasswordDialog();

          this.updatePasswordInApollo(
            agentId.toString(),
            agentEmail,
            newPassword,
            agentWorkspaceId
          );
        },
        error: (error) => {
          console.error('Error changing agent password:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('supervisor.agents.toast.error'),
            detail: error.error?.message || this.translate.instant('supervisor.agents.toast.passwordChangeError')
          });
          this.submittingPassword = false;
        }
      });
  }

  private updatePasswordInApollo(agentId: string, email: string, newPassword: string, workspaceId: string): void {
    this.workspaceService.get_id_mind_x_from_agent(email, workspaceId).subscribe({
      next: (agentUuid: string) => {
        if (agentUuid) {
          this.apollo.use('users').mutate({
            mutation: UPDATE_USER_MUTATION,
            variables: {
              id: agentUuid,
              password: newPassword
            },
            context: {
              headers: this.getWorkspaceAuthorizationHeaders()
            }
          }).subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('supervisor.agents.toast.success'),
                detail: this.translate.instant('supervisor.agents.toast.graphqlPasswordUpdated')
              });
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('supervisor.agents.toast.graphqlError'),
                detail: error.message || this.translate.instant('supervisor.agents.toast.graphqlPasswordError')
              });
            }
          });
        } else {
          console.warn(`No se pudo actualizar la contraseña en GraphQL para ${agentId}`);
        }
      },
      error: (error: any) => {
        console.error('Error al obtener ID del agente:', error);
      }
    });
  }

  private getWorkspaceAuthorizationHeaders(): Record<string, string> {
    const token = localStorage.getItem('token')
      || localStorage.getItem('token-otp')
      || localStorage.getItem('authToken')
      || '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  get emailControl(): AbstractControl {
    return this.agentForm.get('email')!;
  }

  get usernameControl(): AbstractControl {
    return this.agentForm.get('firstName')!;
  }

  get firstNameControl(): AbstractControl {
    return this.agentForm.get('firstName')!;
  }

  get lastNameControl(): AbstractControl {
    return this.agentForm.get('lastName')!;
  }

  get documentControl(): AbstractControl {
    return this.agentForm.get('document')!;
  }

  get versionControl(): AbstractControl {
    return this.agentForm.get('version')!;
  }

  get ipControl(): AbstractControl {
    return this.agentForm.get('ip')!;
  }

  get workspaceControl(): AbstractControl {
    return this.agentForm.get('workspace_id')!;
  }

  get passwordControl(): AbstractControl {
    return this.agentForm.get('password')!;
  }

  get statusControl(): AbstractControl {
    return this.agentForm.get('status')!;
  }

  get enableOtpControl(): AbstractControl {
    return this.agentForm.get('enableOTP')!;
  }

  get newPassword(): AbstractControl {
    return this.passwordForm.get('newPassword')!;
  }

  get confirmPassword(): AbstractControl {
    return this.passwordForm.get('confirmPassword')!;
  }

  get showPasswordMismatch(): boolean {
    return this.confirmPassword.touched && this.passwordForm.hasError('passwordMismatch');
  }

  get hasMinLength(): boolean {
    return this.newPassword.value?.length >= 8;
  }

  get hasUpperCase(): boolean {
    return /[A-Z]/.test(this.newPassword.value || '');
  }

  get hasSpecialChar(): boolean {
    const specialCharsRegex = /[!"$%&}'()*+,\-./:;<=>?@\[\\\]^_{|}~#]/;
    return specialCharsRegex.test(this.newPassword.value || '');
  }

  get showMinLengthError(): boolean {
    return this.newPassword.touched && this.newPassword.hasError('minlength');
  }

  get showUppercaseError(): boolean {
    return this.newPassword.touched && this.newPassword.hasError('noUppercase');
  }

  get showSpecialCharError(): boolean {
    return this.newPassword.touched && this.newPassword.hasError('noSpecialChar');
  }

  get passwordStrengthClass(): string {
    if (!this.newPassword.value) return '';

    const passwordValue = this.newPassword.value;
    let score = 0;

    if (passwordValue.length >= 8) score += 1;
    if (passwordValue.length >= 12) score += 1;
    if (/[A-Z]/.test(passwordValue)) score += 1;
    if (this.hasSpecialChar) score += 1;
    if (/\d/.test(passwordValue)) score += 1;

    if (score >= 4) return 'strong';
    if (score >= 2) return 'medium';
    return 'weak';
  }

  get passwordStrengthText(): string {
    if (!this.newPassword.value) return '';

    switch (this.passwordStrengthClass) {
      case 'strong':
        return this.translate.instant('supervisor.agents.passwordStrength.strong');
      case 'medium':
        return this.translate.instant('supervisor.agents.passwordStrength.medium');
      default:
        return this.translate.instant('supervisor.agents.passwordStrength.weak');
    }
  }
}
